/**
 * Places Grid Section Component
 * Displays places in a 3-column grid layout for the Places tab
 */

import React, { useState, useMemo } from 'react';
import {
  spacing,
  typography,
  typographyColors,
  fontPrimary,
  strokeColors,
  borders,
} from '@/design-system/utils';
import { ActivityCard as ActivityCardType } from '@/constants/chatScreenData';
import { ChatLocation } from '@/types/chatTypes';
import { PlaceGridCard } from './PlaceGridCard';

export interface PlacesGridSectionProps {
  title: string;
  locations: ChatLocation[]; // All valid locations (tourist attractions and businesses)
  onCardClick?: (cardId: string) => void;
  showHeader?: boolean;
}

export const PlacesGridSection: React.FC<PlacesGridSectionProps> = ({
  title,
  locations,
  onCardClick,
  showHeader = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Map all locations to cards
  const cards = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Map to ActivityCard format (includes both tourist attractions and businesses)
    return locations.map((location, index): ActivityCardType => {
      // Handle both image_urls (snake_case) and imageUrls (camelCase) from API
      const imageUrls =
        location.image_urls ||
        (location as unknown as { imageUrls?: string[] }).imageUrls ||
        [];

      // Filter out invalid URLs (empty strings, null, undefined)
      const validImageUrls = imageUrls.filter(
        url => url && typeof url === 'string' && url.trim().length > 0
      );

      // Use index to pick image, but validate that we have enough images
      // If index is out of bounds or no valid images, use the last one
      let selectedImageUrl: string | undefined;
      if (validImageUrls.length > 0) {
        if (index < validImageUrls.length) {
          // Index is valid, use image at that index
          selectedImageUrl = validImageUrls[index];
        } else {
          // Index is out of bounds, use last image in array
          selectedImageUrl = validImageUrls[validImageUrls.length - 1];
        }
      }

      return {
        id: location.place_id
          ? `${location.place_id}_${index}`
          : `location_${index}`,
        title:
          location.title ||
          location.place ||
          location.place_name ||
          'Unknown Location',
        image: selectedImageUrl || '', // Empty placeholder when no images available
        location:
          location.subtitle ||
          location.address ||
          location.locality ||
          'Location not specified',
        rating:
          typeof location.rating === 'number' ? location.rating : undefined,
      };
    });
  }, [locations]);

  // Show all cards in the grid
  const displayedCards = cards;

  if (!cards || cards.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        <h3
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            lineHeight: 'normal',
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.regular,
            color: typographyColors.primary,
            lineHeight: 'normal',
          }}
        >
          No places available at the moment.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
      }}
    >
      {/* Header with title and collapse button */}
      {showHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <h3
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.lg,
              fontWeight: typography.weight.semibold,
              color: typographyColors.primary,
              lineHeight: 'normal',
              margin: 0,
            }}
          >
            {title}
          </h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              backgroundColor: typographyColors.alternate,
              border: `1px solid ${strokeColors.base}`,
              borderRadius: borders.radius.full,
              padding: spacing[2],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '36px',
              height: '36px',
              flexShrink: 0,
            }}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <path
                d='M5 12.5L10 7.5L15 12.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      )}

      {/* Grid container */}
      {(showHeader ? isExpanded : true) && (
        <div style={{ position: 'relative', width: '100%' }}>
          <div
            style={{
              display: 'grid',
              gap: spacing[2], // 8px gap as per Figma
              width: '100%',
            }}
            className='grid-cols-2 md:grid-cols-3'
          >
            {displayedCards.map((card, index) => (
              <PlaceGridCard
                key={`${card.id}_${index}`}
                card={card}
                onClick={onCardClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
